import { BackIcon, ForwardIcon, SearchIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { Checkbox } from '@components/form';
import { Group } from '@components/layout';
import { Typography } from '@components/typography';
import { Stack, useTheme } from '@mui/material';
import { ServerTag, type ServerTagProps } from '../ServerTag';

export const ResourceTransferListButtons = ({
  onAdd,
  onRemove,
  isVertical,
  addDisabled,
  removeDisabled,
}: {
  onAdd: () => void;
  onRemove: () => void;
  isVertical: boolean;
  addDisabled: boolean;
  removeDisabled: boolean;
}) => {
  const { palette } = useTheme();
  return (
    <Stack
      direction={isVertical ? 'row-reverse' : 'column'}
      alignItems="center"
      justifyContent="center"
      gap={1}
    >
      <IconButton
        Icon={ForwardIcon}
        tooltip="Remove selected items"
        disabled={removeDisabled}
        onClick={onRemove}
        size={24}
        color={palette.foreground.paper.default}
        sx={{ transform: isVertical ? 'rotate(90deg)' : 'none' }}
      />
      <IconButton
        Icon={BackIcon}
        tooltip="Add selected items"
        disabled={addDisabled}
        onClick={onAdd}
        size={24}
        color={palette.foreground.paper.default}
        sx={{ transform: isVertical ? 'rotate(90deg)' : 'none' }}
      />
    </Stack>
  );
};

type ResourceListItemProps<T> = {
  resource: Partial<T>;
  tag?: string;
  tagProps?: ServerTagProps;
  idKey: keyof T;
  selected: boolean;
  onToggle: (id: string) => void;
  onViewDetails?: (resource: Partial<T>) => void;
};

export const ResourceListItem = <T extends { name?: string }>({
  resource,
  tag,
  idKey,
  tagProps,
  selected,
  onToggle,
  onViewDetails,
}: ResourceListItemProps<T>) => {
  return (
    <Group
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      sx={{
        px: 3,
        py: 2,
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'action.hover' },
        borderRadius: 1,
      }}
      onClick={() => onToggle(resource[idKey] as string)}
    >
      <Group alignItems="center" gap={3}>
        <Checkbox checked={selected} sx={{ mr: 0, height: 'min-content' }} />
        <Typography variant="body2" handleOverFlow sx={{ flex: 1, minWidth: 0 }}>
          {resource.name}
        </Typography>
      </Group>
      <Group alignItems="center" justifyContent="flex-end" flex={1} flexShrink={0} gap={1}>
        {tag && <ServerTag color="default" variant="caption2" label={tag} {...tagProps} />}
        {onViewDetails && (
          <IconButton
            Icon={SearchIcon}
            tooltip="Details"
            onClick={(event) => {
              event.stopPropagation();
              onViewDetails(resource);
            }}
            size={16}
            variant="default"
          />
        )}
      </Group>
    </Group>
  );
};
